import type { AuthSession, LicenceStatus } from "../api/types";

/** Machine d'état du portail des comptes (docs/17 §8.1) : ce que montre `AuthGate` pour une licence, une session et
 *  l'application déjà montée (ou non) dans ce document. Fonctions pures, éprouvées sans navigateur.
 *
 *  - Mode hérité (licence jamais basculée, serveur sans comptes, contrôle de licence désactivé) : l'application, telle
 *    qu'aujourd'hui, sans aucune requête de plus (`accountsRelevant` faux).
 *  - Avant le montage : proposition de bascule, écrans de connexion, de code, de récupération, notice, session
 *    verrouillée au chargement de la page.
 *  - Après le montage, l'identité ne change plus (§4.8) : verrouillage, expiration ou fermeture posent une surcouche
 *    PAR-DESSUS l'application ; une session ouverte pour un autre compte fait recharger la page (`switch`), sans rien
 *    rejouer. */

export type AuthViewName =
  | "wait"
  | "legacy"
  | "fallback"
  | "bootstrap"
  | "recovery"
  | "login"
  | "redeem"
  | "recover"
  | "notice"
  | "locked"
  | "switch"
  | "app";

/** Surcouche posée sur l'application montée, à identité fixe. */
export type OverlayName = "locked" | "expired" | "closed";

/** Écran choisi par les liens de l'écran de connexion. */
export type SignInScreen = "login" | "redeem" | "recover";

/** Compte pour lequel l'application est montée : son identité ne change plus jusqu'au rechargement. */
export interface MountedAccount {
  id: string;
  login: string;
  display_name: string;
  scope_ref: string;
}

export interface AuthView {
  view: AuthViewName;
  overlay: OverlayName | null;
}

/** La licence a-t-elle affaire aux comptes nominatifs ? Faux : mode hérité, rien ne change (aucune requête de plus). */
export function accountsRelevant(licence: LicenceStatus | null | undefined): boolean {
  const accounts = licence?.accounts;
  if (!licence?.enforced || !licence.licensed || !accounts) return false;
  return accounts.mode !== "legacy" || (accounts.bootstrap ?? "none") !== "none";
}

/** Amorçage proposé par la session ou, à défaut, par l'état de la licence. */
export function bootstrapOffer(session: AuthSession | null | undefined, licence: LicenceStatus | null | undefined): "none" | "offer" | "required" {
  return session?.bootstrap ?? licence?.accounts?.bootstrap ?? "none";
}

export interface AuthInput {
  licence: LicenceStatus | null | undefined;
  /** Session lue : `undefined` tant qu'elle se lit, `null` si les routes des comptes sont absentes (serveur en `off`). */
  session: AuthSession | null | undefined;
  mounted: MountedAccount | null;
  /** Surcouche posée par un refus (401, inactivité) avant que la session ne soit relue. */
  hint: OverlayName | null;
  /** « Plus tard » choisi pendant cette session du navigateur. */
  later: boolean;
  screen: SignInScreen;
  /** Code de secours à montrer (après l'amorçage ou la récupération) : passe avant tout le reste. */
  recovery: boolean;
}

/** Surcouche d'une session qui n'est plus ouverte, pour l'application montée. */
function closedOverlay(session: AuthSession, hint: OverlayName | null): OverlayName {
  if (session.reason === "expired" || hint === "expired") return "expired";
  return "closed";
}

export function authView(input: AuthInput): AuthView {
  const { session, mounted, hint } = input;
  // Application montée : elle le reste ; seule une session d'un autre compte la fait recharger.
  if (mounted) {
    if (!accountsRelevant(input.licence) || !session) return { view: "app", overlay: session === undefined ? hint : null };
    const other = Boolean(session.account && session.account.id !== mounted.id);
    switch (session.state) {
      case "open":
      case "notice":
        return other ? { view: "switch", overlay: null } : { view: "app", overlay: hint };
      case "locked":
        return other ? { view: "switch", overlay: null } : { view: "app", overlay: "locked" };
      case "login":
        return { view: "app", overlay: closedOverlay(session, hint) };
      default:
        // Retour au mode hérité, repli ou amorçage : plus de session à reprendre, l'application reste en place.
        return { view: "app", overlay: null };
    }
  }
  if (!accountsRelevant(input.licence)) return { view: "legacy", overlay: null };
  if (input.recovery) return { view: "recovery", overlay: null };
  if (session === undefined) return { view: "wait", overlay: null };
  // Routes des comptes absentes (indicateur `off`) : mode hérité, ou repli d'une licence déjà basculée (bandeau).
  if (session === null) return { view: input.licence?.accounts?.mode === "fallback" ? "fallback" : "legacy", overlay: null };
  switch (session.state) {
    case "legacy":
      return { view: "legacy", overlay: null };
    case "fallback":
      return { view: "fallback", overlay: null };
    case "bootstrap": {
      const offer = bootstrapOffer(session, input.licence);
      if (offer === "none" || (offer === "offer" && input.later)) return { view: "legacy", overlay: null };
      return { view: "bootstrap", overlay: null };
    }
    case "notice":
      return { view: "notice", overlay: null };
    case "locked":
      return { view: session.account ? "locked" : "login", overlay: null };
    case "open":
      return { view: session.account && session.scope_ref ? "app" : "login", overlay: null };
    default:
      return { view: input.screen, overlay: null };
  }
}

/** Application rendue sans compte dans ce document (mode hérité, repli), et mode des comptes lu à ce moment-là. */
export interface BareApp {
  shown: "legacy" | "fallback";
  mode: string;
}

/** L'application rendue sans compte le reste jusqu'au rechargement (docs/17 §4.8, §8.3) :
 *  - un code d'amorçage émis en cours de travail ne la remplace pas : la proposition attend l'ouverture suivante ;
 *  - des comptes ouverts (ou rétablis) depuis un autre poste font ranger les saisies et recharger (`reload`), jamais
 *    remplacer l'application par l'écran de connexion. Le mode lu à l'ouverture compte : une licence déjà en mode
 *    comptes au chargement (serveur en « off ») ne recharge pas en boucle. */
export function bareApp(
  previous: BareApp | null,
  input: { mounted: boolean; relevant: boolean; view: AuthViewName; mode: string },
): { bare: BareApp | null; shown: BareApp["shown"] | null; reload: boolean } {
  if (input.mounted) return { bare: previous, shown: null, reload: false };
  const now: BareApp["shown"] | null = !input.relevant || input.view === "legacy" ? "legacy" : input.view === "fallback" ? "fallback" : null;
  const bare: BareApp | null = now ? { shown: now, mode: previous?.mode ?? input.mode } : previous;
  return { bare, shown: bare?.shown ?? null, reload: Boolean(bare && input.mode === "accounts" && bare.mode !== "accounts") };
}

/** Refus de session d'une requête (401) à prendre en compte : pas s'il vient d'une requête partie avant la dernière
 *  reprise (relecture en vol pendant le déverrouillage), ni si la même surcouche est déjà posée (les relectures
 *  automatiques continuent dessous). `sentAt` 0 : heure inconnue, refus pris. */
export function refusalTaken(sentAt: number, resumedAt: number, hint: OverlayName | null, next: OverlayName): boolean {
  if (sentAt > 0 && sentAt < resumedAt) return false;
  return hint !== next;
}

/** Surcouche posée par un refus du serveur, alors que la session relue APRÈS lui est ouverte pour la même personne :
 *  à lever (refus tardif). Une surcouche posée par ce navigateur (inactivité, menu, autre onglet) ne se lève qu'au mot
 *  de passe, même si le serveur n'a pas pu verrouiller. */
export function serverHintStale(
  state: { hint: OverlayName | null; hintFromServer: boolean; hintAt: number },
  session: AuthSession | null | undefined,
  readAt: number,
  mountedId: string | null,
): boolean {
  if (!mountedId || !state.hint || !state.hintFromServer || readAt <= state.hintAt) return false;
  return session?.state === "open" && session.account?.id === mountedId;
}

/** D'une surcouche à la suivante : « snapshot » à son apparition (saisies recopiées dans le navigateur, comme pour la
 *  licence perdue, audit M6), « resume » à son retrait (files relancées pour la même personne). */
export function overlayStep(before: OverlayName | null, after: OverlayName | null): "snapshot" | "resume" | null {
  if (!before && after) return "snapshot";
  if (before && !after) return "resume";
  return null;
}

/** Compte monté d'après une session ouverte (ou verrouillée) ; null si elle ne désigne pas de compte. */
export function mountedFrom(session: AuthSession | null | undefined): MountedAccount | null {
  const account = session?.account;
  if (!account || !session?.scope_ref) return null;
  return { id: account.id, login: account.login, display_name: account.display_name, scope_ref: session.scope_ref };
}

/** Initiales d'un grade et nom (« CNE MARTIN » → « CM »), pour la puce du compte sur un écran étroit. */
export function initials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0]!, words[words.length - 1]!] : words;
  return letters.map((w) => w[0]!.toUpperCase()).join("") || "?";
}
